import React, { useState, useEffect } from 'react';
import {
  Table, Card, Tag, Input, Select, Button, Space, message, Row, Col, Progress, Tooltip, Popconfirm,
} from 'antd';
import { SearchOutlined, ReloadOutlined, HomeOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const PublicPages = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [bmList, setBmList] = useState([]);
  const [filters, setFilters] = useState({
    bm_id: undefined,
    status: undefined,
    search: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const fetchBmList = async () => {
    try {
      const res = await api.get('/business-managers/', { params: { limit: 100 } });
      setBmList(res.data || []);
    } catch (error) {
      console.error('Fetch BM list error:', error);
    }
  };

  const fetchPages = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (filters.bm_id) params.bm_id = filters.bm_id;
      if (filters.status) params.status = filters.status;
      if (filters.search?.trim()) params.search = filters.search.trim();

      const res = await api.get('/business-managers/pages/list', { params });
      const data = res.data || {};
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setPagination({ current: page, pageSize });
    } catch (error) {
      message.error(error.response?.data?.detail || '获取公共主页列表失败');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBmList();
    fetchPages(1, pagination.pageSize);
  }, []);

  const handleSearch = () => fetchPages(1, pagination.pageSize);

  const handleReset = () => {
    const reset = { bm_id: undefined, status: undefined, search: '' };
    setFilters(reset);
    setPagination((p) => ({ ...p, current: 1 }));
    setLoading(true);
    api
      .get('/business-managers/pages/list', { params: { skip: 0, limit: pagination.pageSize } })
      .then((res) => {
        setItems(res.data?.items || []);
        setTotal(res.data?.total ?? 0);
      })
      .catch((error) => {
        message.error(error.response?.data?.detail || '获取公共主页列表失败');
      })
      .finally(() => setLoading(false));
  };

  const handleToggleStatus = async (record) => {
    const nextStatus = record.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/business-managers/pages/${record.id}/status`, {
        status: nextStatus,
      });
      message.success(nextStatus === 'paused' ? '已暂停' : '已启用');
      fetchPages();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  const statusLabel = (s) => {
    if (s === 'active') return '启用';
    if (s === 'paused') return '已暂停';
    return s || '-';
  };

  const usagePercent = (record) => {
    const limit = record.active_ad_limit || 250;
    if (!limit) return 0;
    return Math.min(100, Math.round(((record.active_ad_count || 0) / limit) * 100));
  };

  const columns = [
    {
      title: '主页名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: '主页 ID',
      dataIndex: 'page_id',
      key: 'page_id',
      width: 160,
      render: (id) => <span style={{ fontFamily: 'monospace' }}>{id}</span>,
    },
    {
      title: '所属 BM',
      key: 'bm',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={`BM ID: ${record.facebook_bm_id || '-'}`}>
          <span>{record.bm_name || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: '在投占用',
      key: 'usage',
      width: 200,
      render: (_, record) => {
        const pct = usagePercent(record);
        const status =
          record.is_full ? 'exception' : pct >= 80 ? 'active' : 'normal';
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <span>
              {record.active_ad_count ?? 0} / {record.active_ad_limit ?? 250}
              {record.is_full && (
                <Tag color="red" style={{ marginLeft: 8 }}>
                  已满
                </Tag>
              )}
            </span>
            <Progress percent={pct} size="small" status={status} showInfo={false} />
          </Space>
        );
      },
    },
    {
      title: '剩余额度',
      dataIndex: 'remaining',
      key: 'remaining',
      width: 90,
      align: 'center',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : s === 'paused' ? 'orange' : 'default'}>
          {statusLabel(s)}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        record.status === 'active' ? (
          <Popconfirm
            title="暂停后不会参与自动分配与广告创建选页，确定暂停？"
            onConfirm={() => handleToggleStatus(record)}
            okText="暂停"
            cancelText="取消"
          >
            <Button type="link" size="small" icon={<PauseCircleOutlined />} danger>
              暂停
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            启用
          </Button>
        )
      ),
    },
    {
      title: '统计时间',
      dataIndex: 'usage_calculated_at',
      key: 'usage_calculated_at',
      width: 170,
      render: (t) => (t ? String(t).replace('T', ' ').slice(0, 19) : '-'),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <HomeOutlined />
            公共主页
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => fetchPages()}>
            刷新
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Select
              allowClear
              placeholder="按 BM 筛选"
              style={{ width: '100%' }}
              value={filters.bm_id}
              onChange={(v) => setFilters({ ...filters, bm_id: v })}
            >
              {bmList.map((bm) => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              allowClear
              placeholder="状态"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => setFilters({ ...filters, status: v })}
            >
              <Option value="active">启用</Option>
              <Option value="paused">已暂停</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Input
              placeholder="搜索主页名称 / ID"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 个主页`,
            onChange: (page, pageSize) => fetchPages(page, pageSize),
          }}
          scroll={{ x: 960 }}
        />
      </Card>
    </div>
  );
};

export default PublicPages;
